import React from 'react';
import useBuilderStore from '../store/useBuilderStore';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const BlockRenderer = ({ block, columnId, index, isPreview = false, idPrefix = '', scale = 1 }) => {
  const { selectedElement, setSelectedElement } = useBuilderStore();
  const isSelected = selectedElement?.id === block.id;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ 
    id: `${idPrefix}${block.id}`, 
    data: { type: 'block', columnId, index, originalId: block.id }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 100 : 1,
  };

  const renderContent = () => {
    let alignmentStyle = { textAlign: 'center' };
    let gradualPadding = {};
    
    if (typeof block.align === 'number') {
      if (block.align > 0) gradualPadding = { paddingLeft: `${block.align}%` };
      else if (block.align < 0) gradualPadding = { paddingRight: `${Math.abs(block.align)}%` };
    } else {
      alignmentStyle.textAlign = block.align || (["image", "button", "social"].includes(block.type) ? "center" : "left");
    }
    
    const blockStyle = {
      ...alignmentStyle,
      ...gradualPadding,
      paddingTop: `${block.marginTop || 0}px`,
      paddingBottom: `${block.marginBottom || 0}px`,
      marginLeft: `${block.marginLeft || 0}px`,
      marginRight: `${block.marginRight || 0}px`,
    };

    const innerPaddingStyle = {
      paddingTop: `${block.innerPaddingTop || 0}px`,
      paddingBottom: `${block.innerPaddingBottom || 0}px`,
      paddingLeft: `${block.innerPaddingLeft || 0}px`,
      paddingRight: `${block.innerPaddingRight || 0}px`,
    };

    const blockWidth = block.width ? `${block.width}px` : '100%';

    switch (block.type) {
      case 'heading':
        return (
          <div style={blockStyle}>
            <h1 style={{
              margin: 0,
              color: block.color,
              fontSize: `${block.fontSize}px`,
              fontWeight: block.isBold ? 'bold' : 'normal',
              fontStyle: block.isItalic ? 'italic' : 'normal',
              textDecoration: block.isUnderline ? 'underline' : 'none',
              display: 'inline-block',
              width: blockWidth,
              ...innerPaddingStyle
            }}>
              {block.text || 'Heading'}
            </h1>
          </div>
        );
      case 'text':
        return (
          <div style={blockStyle}>
            <p style={{
              margin: 0,
              color: block.color,
              fontSize: `${block.fontSize}px`,
              fontWeight: block.isBold ? 'bold' : 'normal',
              fontStyle: block.isItalic ? 'italic' : 'normal',
              textDecoration: block.isUnderline ? 'underline' : 'none',
              whiteSpace: 'pre-wrap',
              display: 'inline-block',
              width: blockWidth,
              ...innerPaddingStyle
            }}>
              {block.text || 'Text content...'}
            </p>
          </div>
        );
      case 'image':
        return (
          <div style={blockStyle}>
            <img 
              src={block.src || "https://via.placeholder.com/600x250/2a0a4a/ffffff?text=Image"} 
              alt="Block" 
              style={{
                width: blockWidth,
                maxWidth: '100%',
                borderRadius: block.isHero ? '0' : '8px',
                display: 'inline-block',
                ...innerPaddingStyle
              }}
            />
          </div>
        );
      case 'button':
        const btnBackground = block.useGradient
          ? `linear-gradient(to right, ${block.gradientStart}, ${block.gradientEnd})`
          : block.bgColor;
        return (
          <div style={blockStyle}>
            <button style={{
              padding: '12px 24px',
              borderRadius: '6px',
              border: 'none',
              background: btnBackground,
              backgroundColor: block.bgColor,
              color: block.color,
              fontSize: `${block.fontSize}px`,
              fontWeight: block.isBold ? 'bold' : 'normal',
              cursor: 'pointer',
              width: blockWidth,
              ...innerPaddingStyle
            }}>
              {block.text || 'Button'}
            </button>
          </div>
        );
      case 'divider':
        return (
          <div style={{ padding: `${block.paddingTop}px 0 ${block.paddingBottom}px 0` }}>
            <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: 0 }} />
          </div>
        );
      case 'social':
        return (
          <div style={blockStyle}>
            <div style={{ display: 'inline-flex', gap: '16px', ...innerPaddingStyle }}>
              {block.socials?.map(s => (
                <img key={s.id} src={s.iconUrl} alt={s.platform} style={{ width: `${block.iconSize}px`, height: `${block.iconSize}px` }} />
              ))}
              {(!block.socials || block.socials.length === 0) && <span style={{ color: '#94a3b8', fontSize: '12px' }}>Social Icons</span>}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`relative group border-2 ${isDragging ? '' : 'transition-all'} ${isSelected ? 'border-[#8B3DFF] bg-[#8B3DFF]/5' : 'border-transparent hover:border-slate-200'} cursor-move`}
      onClick={(e) => {
        e.stopPropagation();
        setSelectedElement({ type: 'block', id: block.id, parentId: columnId });
      }}
    >
      {/* Component Label */}
      <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-[#8B3DFF] text-white text-[8px] font-black uppercase rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-50 pointer-events-none`}>
         {block.type}
      </div>

      <div className={`${!isPreview ? 'p-2' : 'p-1'} select-none`}>
        {renderContent()}
      </div>

      {/* Selection Glow */}
      {isSelected && (
        <div className="absolute inset-0 pointer-events-none border-2 border-[#8B3DFF] rounded-sm animate-pulse opacity-30 shadow-[0_0_15px_rgba(139,61,255,0.2)]" />
      )}
    </div>
  );
};

export default BlockRenderer;
